import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { missingCardMessage } from 'modules/clients/constants';
import get from 'lodash/get';
import intersection from 'lodash/intersection';
import sortBy from 'lodash/sortBy';
import { PRICE_ADJUST } from 'constants';
import { alphabet } from 'utils';

const requiredKeys = ['firstName', 'lastName', 'line1', 'city', 'country', 'state', 'zip', 'phone'];
/**
 * Funtion to validate all required fields on billingAddress
 * @param {Object<string, string>} billingAddress
 * @return Boolean
 */
export const validateBillingAddress = (billingAddress) => {
  const uncheckedKeys = requiredKeys.reduce((arr, key) => {
    if (!billingAddress[key]) {
      arr.push(key);
    }
    return arr;
  }, []);

  return uncheckedKeys.length === 0;
};

/**
 * Function to validate payment card
 * @param {Object} [payments]
 * @return Boolean
 */
export const validatePaymentCard = ({ payments }) => {
  const payment = payments.find((p) => p.selected) || payments.find((p) => p.defaultCard) || {};
  const { billingAddress } = payment;

  if (!isEmpty(billingAddress)) {
    return validateBillingAddress(billingAddress);
  }

  return false;
};

/**
 * Function to get intersection between kiNumbersForInCircle and paymentMethods
 * @param {Array<string>} kiNumbersForInCircle
 * @param {Array<{ keyIdentifier: string }>} paymentMethods
 * @return Array<string>
 */
export const getKiNumbers = (kiNumbersForInCircle, paymentMethods) => {
  return kiNumbersForInCircle
    ? paymentMethods.reduce((acc, { keyIdentifier }) => {
        if (kiNumbersForInCircle.includes(keyIdentifier)) {
          return acc.concat(keyIdentifier);
        }
        return acc;
      }, [])
    : // fallback to paymentMethods only
      paymentMethods.reduce((acc, { keyIdentifier }) => {
        if (keyIdentifier) {
          return acc.concat(keyIdentifier);
        }
        return acc;
      }, []);
};

/**
 * Function returns error message for order submit request
 * @param {Object} err
 * @return {String} errorMessage
 */
export const extractSubmitErrorMessage = (err) => {
  let errorMessage = get(err, 'response.data.message', 'Something went wrong');
  if (errorMessage.match(/instance.soldTo.address requires property/g)?.length > 1) {
    errorMessage = missingCardMessage;
  }
  return errorMessage;
};

export const convertFilterValueToSeconds = (value) => {
  switch (value) {
    case 'three-days':
      return moment()
        .subtract(3, 'days')
        .format('X');

    case 'week':
      return moment()
        .subtract(1, 'weeks')
        .format('X');

    case 'month':
    case 'more-than-a-month':
      return moment()
        .subtract(30, 'days')
        .format('X');

    case 'quarter':
      return moment()
        .subtract(90, 'days')
        .format('X');

    default:
      return '';
  }
};

export const getAdjustedPrice = (productPrice, adjustment, priceType) => {
  if (!productPrice || !adjustment) {
    return null;
  }

  let adjustedPrice;
  const { DOLLAR, PERCENT, OVERRIDE } = PRICE_ADJUST.TYPES_CHARS;
  switch (priceType) {
    case DOLLAR:
      adjustedPrice = productPrice - adjustment;
      break;
    case PERCENT:
      adjustedPrice = productPrice - (productPrice * adjustment) / 100;
      break;
    case OVERRIDE:
      adjustedPrice = Number(adjustment);
      break;
    default:
      break;
  }
  return adjustedPrice;
};

/**
 * Function returns array of data objects for clients, who's id's is passed as selectedIds.
 * RTC (reasons to contact) service returns the reasons, split by categories.
 * Each of those categories has 'client' property.
 * @categories {Array<{ keyIdentifier: string }>}
 * @selectedIds {Array<string>}
 * @return {Array<{ keyIdentifier: string }>} selectedClients
 */
export const getClientsByIdsFromRTCCategories = (categories, selectedIds) => {
  return categories
    .filter((category) => selectedIds.includes(category.customer.CMDId))
    .map((category) => {
      const { CMDId, firstName, lastName, email } = category.customer;
      return {
        profileId: CMDId,
        firstName,
        lastName,
        email,
      };
    });
};

export const getRTCExpirationDate = () => {
  return moment()
    .day(6)
    .format('MMMM D'); // next saturday
};

export const getNormalizedValue = (current, total) => {
  if (total === 0) {
    return 0;
  }
  return (current / total) * 100;
};

export const getMappedRTCCategories = (categories, data) => {
  const categoriesData = categories.map(({ name, numberOfClients: total }) => ({
    name,
    total,
    done: data.filter(({ completed, customerRTCType }) => {
      return (
        typeof customerRTCType === 'string' &&
        typeof name === 'string' &&
        customerRTCType.toLowerCase() === name.toLowerCase() &&
        completed
      );
    }).length,
  }));

  const { total, done } = categoriesData.reduce(
    (acc, { total, done }) => {
      acc.total += total;
      acc.done += done;
      return acc;
    },
    { total: 0, done: 0 },
  );

  const summaryData = { total, done };

  return {
    categoriesData,
    summaryData,
  };
};

/**
 * Function returns map of queryParams to be sent in customer service request
 * while fetching client's list of available filter facets.
 * @params { keyIdentifier: string }
 * @returnFacets Boolean
 * @return { keyIdentifier: string } filteredParams
 */
export const mapFilteredParams = (params, returnFacets = false) => {
  const faucetsFilters = ['departments', 'designers'];
  const filteredParams = Object.keys(params)
    .filter((paramName) => (paramName !== 'searchLevel' && params[paramName]) || params[paramName] === 0)
    .reduce(
      (result, paramName) => {
        if (faucetsFilters.includes(paramName)) {
          const selectedParams = params[paramName].split(',').map(encodeURIComponent);
          if (!selectedParams.length) return result;

          const currentParam = `${paramName}:${selectedParams.join(';')}`;
          const applyFacets = result.applyFacets ? `${result.applyFacets},${currentParam}` : currentParam;
          return {
            ...result,
            applyFacets,
          };
        }

        return { ...result, [paramName]: params[paramName] };
      },
      { returnFacets },
    );

  if (filteredParams.shoppedFrom) {
    filteredParams.shoppedFrom = convertFilterValueToSeconds(filteredParams.shoppedFrom);
  }
  if (filteredParams.contactedFrom) {
    filteredParams.contactedFrom = convertFilterValueToSeconds(filteredParams.contactedFrom);
  }
  if (filteredParams.contactedTill) {
    filteredParams.contactedTill = convertFilterValueToSeconds(filteredParams.contactedTill);
  }

  return filteredParams;
};

export const fillEmptyLetters = (alphabetMap) => {
  const alphabetWithAllLetters = alphabet.reduce((acc, letter) => {
    if (alphabetMap[letter] !== undefined) {
      acc[letter] = alphabetMap[letter];
    } else {
      acc[letter] = null;
    }

    return acc;
  }, {});

  if (alphabetMap.A !== null && alphabetMap.A > 0) {
    alphabetWithAllLetters['#'] = 0;
  } else {
    alphabetWithAllLetters['#'] = null;
  }

  return alphabetWithAllLetters;
};

export const getVisibleRTCItems = (categoryRTCItems, filters) => {
  const { actionTypes, badges, assets } = filters;
  let result = categoryRTCItems;

  if (actionTypes?.length) {
    result = result.filter(({ actionType }) => actionTypes.includes(actionType));
  }

  if (badges?.length) {
    result = result.filter((item) => !intersection(badges, item.badges).length);
  }

  if (assets?.length) {
    result = result.filter((item) => intersection(assets, item.assets).length);
  }

  return result;
};

export const shouldClearSortBy = (currentFilter, currentSort, newFilter) => {
  const sortedByPointBalance = currentSort.includes('pointBalance');
  const sortedByPromoBalance = currentSort.includes('promoBalance');
  const onlyOneFilterAsset = newFilter.withAssets && newFilter.withAssets.split(',').length === 1;
  const filterPromoAssetDifferentSorting = newFilter.withAssets === 'PROMO' && sortedByPointBalance;
  const filterPointAssetDifferentSorting = newFilter.withAssets === 'POINT' && sortedByPromoBalance;

  if (
    currentSort &&
    ((currentFilter.withAssets && !newFilter.withAssets && (sortedByPointBalance || sortedByPromoBalance)) ||
      (onlyOneFilterAsset && (filterPromoAssetDifferentSorting || filterPointAssetDifferentSorting)))
  ) {
    return true;
  }

  return false;
};

export const filterKiNumbersByPlccConversion = (kiNumbers) => {
  let filteredKiNumbers = [];

  if (kiNumbers?.length) {
    filteredKiNumbers = kiNumbers.filter((item) => {
      return item.length === 16;
    });
  }

  return filteredKiNumbers;
};

export const formatReminderIsoDate = (value) => {
  if (!value) return null;

  switch (value) {
    case 'Tomorrow':
      return moment()
        .add(1, 'day')
        .format('YYYY-MM-DD');
    case 'Next Week':
      return moment()
        .add(1, 'week')
        .format('YYYY-MM-DD');
    case 'Next Month':
      return moment()
        .add(1, 'month')
        .format('YYYY-MM-DD');
    default:
      return moment(value).format('YYYY-MM-DD');
  }
};

/**
 * Returns reminders to be shown on client account tab.
 * @params { reminders: Array<{ ReminderPropType }> }
 * @return { visibleReminders: Array<{ ReminderPropType }> }
 */

export const getClientAccountSectionReminders = (reminders) => {
  const visibleRemindersCount = 3;
  return sortBy(reminders, [
    ({ completed }) => completed,
    ({ reminderIsoDate }) => (reminderIsoDate ? moment(reminderIsoDate) : ''),
    ({ createdIsoDateTime }) => moment(createdIsoDateTime),
  ]).slice(0, visibleRemindersCount);
};
